import React, { PropsWithChildren, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import onboardingDataReducer from "./onboardingDataReducer";
import { GlobalContext } from "../../../../GlobalContext";
import { OnboardingActionDispatch, OnboardingData } from "./model";
import { useObserver } from "mobx-react";
import useAnalyticsDispatch from "../../../../analytics/useAnalyticsDispatch";

const ViewContext = React.createContext<OnboardingData>(null);
const UpdateContext = React.createContext<OnboardingActionDispatch>(null);

function useOnboardingData() {
  return useContext(ViewContext);
}

function useOnboardingUpdate() {
  return useContext(UpdateContext);
}

function OnboardingDataConsumer(props: { children: (onboardingData: OnboardingData) => React.ReactNode }) {
  const data = useOnboardingData();
  return useObserver(() => <>{props.children(data)}</>);
}

function OnboardingUpdateConsumer(props: {
  children: (onboardingUpdate: OnboardingActionDispatch) => React.ReactNode;
}) {
  const onboardingUpdate = useOnboardingUpdate();
  return useObserver(() => <>{props.children(onboardingUpdate)}</>);
}

const defaultOnboardingValue: OnboardingData = { template: null, input: null, hidden: false, worksheet: null };

function OnboardingContextProvider(props: PropsWithChildren<{ id: string }>) {
  const { userPreferences } = useContext(GlobalContext);
  const key = useMemo(() => `onboarding${btoa(props.id)}`, [props.id]);
  const [data, dispatch] = useReducer(onboardingDataReducer, defaultOnboardingValue, (defaultValue) => {
    const storage = userPreferences.get();
    return storage[key] ?? storage["onboarding"] ?? defaultValue;
  });
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) {
      userPreferences.upsertKey(key, data);
    } else {
      loaded.current = true;
    }
  }, [data, key]);

  return (
    <ViewContext.Provider value={data}>
      <UpdateContext.Provider value={useAnalyticsDispatch("onboarding", dispatch)}>
        {props.children}
      </UpdateContext.Provider>
    </ViewContext.Provider>
  );
}

export {
  useOnboardingData,
  useOnboardingUpdate,
  OnboardingContextProvider,
  OnboardingDataConsumer,
  OnboardingUpdateConsumer,
};
